import { DatePickerInput, type DateValue } from "@mantine/dates";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEntryDate, setFeature } from "src/redux/slices/generatorSlice";
import { convertDDMMtoMMDD } from "src/utils/timeUtils/convertDateFormat";

import { useInputProps } from "../hooks/useInputProps";
import { rem } from "@mantine/core";
import { useTriggerAutoSave } from "../hooks/useTriggerAutoSave";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import useAmbNewVoyage from "../hooks/useAmbNewVoyage";

dayjs.extend(weekOfYear);

interface IDataPicker {
  id: string;
  externalValue: string;
  required: boolean;
  cellId?: string;
  now?: boolean;
  isFirstRow?: boolean;
  forwardDate?: boolean;
  futureDate?: boolean;
  showWeekday?: boolean;
  tag?: string;
  class?: string;
  rowIndex?: number;
  feature?: string;
  isShared?: boolean;
}

const DatePicker = ({
  id,
  externalValue,
  required,
  cellId,
  now,
  isFirstRow,
  forwardDate,
  futureDate,
  showWeekday,
  rowIndex,
  feature,
  tag,
  class: category,
  isShared,
}: IDataPicker) => {
  const { entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  const dispatch = useDispatch();
  const triggerAutoSave = useTriggerAutoSave();
  // For shared sections with entry-date, use the main entry date value and disable the picker
  const shouldUseMainEntryDate = isShared && cellId === "entry-date";
  const displayValue = shouldUseMainEntryDate
    ? entrydate
      ? convertDDMMtoMMDD(entrydate)
      : null
    : (convertDDMMtoMMDD(externalValue) ??
      (cellId === "entry-date" && now ? new Date() : null));

  const { onChange, ...inputProps } = useInputProps(id, displayValue, required);
  const inputRef = useRef<HTMLButtonElement | null>(null);

  const { getValues, setValue } = useFormContext();
  const { onVoyageChange, modalElement } = useAmbNewVoyage({
    tag,
    getValues,
    setValue,
    rowIndex,
  });

  // Get logbook refs context props for navigation
  const logbookRefsProps = useLogbookRefsContext(id);

  const maxDate = useMemo(
    () =>
      futureDate
        ? undefined
        : forwardDate
          ? new Date(new Date().getTime() + 86400000)
          : new Date(),
    [forwardDate, futureDate],
  );

  useEffect(() => {
    if (cellId !== "entry-date" || !isFirstRow || !inputProps.value || isShared)
      return;

    const selectedDate = dayjs(inputProps.value);
    if (selectedDate === entrydate) return;

    dispatch(setEntryDate(selectedDate.format("DD MMM YYYY")));
  }, [isFirstRow, inputProps.value, dispatch, entrydate, cellId, isShared]);

  // Update shared entry-date fields when main entry date changes
  useEffect(() => {
    if (shouldUseMainEntryDate && entrydate) {
      const mainEntryDateValue = convertDDMMtoMMDD(entrydate);
      if (mainEntryDateValue && mainEntryDateValue !== inputProps.value) {
        onChange(mainEntryDateValue);
      }
    }
  }, [entrydate, shouldUseMainEntryDate, onChange, inputProps.value]);

  const handleChange = (v: DateValue) => {
    // Prevent any changes for shared entry-date fields (they should be disabled anyway)
    if (shouldUseMainEntryDate) return;

    onChange(v);
    if (isShared) return; // Prevent state updates for other shared fields

    if (feature === "autoday") {
      dispatch(
        setFeature({
          key: "autoday",
          feature: v ? dayjs(v).format("dddd") : "",
        }),
      );
    }
    if (cellId === "entry-date" && v)
      dispatch(setFeature({ key: "autoweek", feature: dayjs(v).week() }));

    onVoyageChange(v);
    triggerAutoSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    logbookRefsProps.onKeyDown(e);
  };
  return (
    <>
      {modalElement}
      <DatePickerInput
        miw={rem(150)}
        className={`${category ?? ""} rowIndex-${rowIndex ?? ""} ${tag ?? ""}`}
        data-id={id}
        data-unit={"date"}
        key={id}
        id={id}
        mx="auto"
        maxDate={maxDate}
        valueFormat={`DD MMM YYYY ${showWeekday ? "dddd" : ""}`}
        dropdownType="popover"
        popoverProps={{
          position: "top-start",
          withinPortal: true,
          zIndex: 1000,
        }}
        {...inputProps}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        ref={el => {
          inputRef.current = el;
          logbookRefsProps.ref(el);
        }}
        clearable={!shouldUseMainEntryDate}
        disabled={shouldUseMainEntryDate}
      />
    </>
  );
};

export default DatePicker;
